import {
  BodyText,
  Card,
  Checkbox,
  ChoiceList,
  Code,
  Heading,
  Input,
  Radio,
  Select,
  TabPanel,
  Textarea,
} from '@postscript/components';
import {
  StyledCardGrid,
  StyledTabPanelIntro,
  StyledTabPanelText,
} from '../../Layout/SimpleStyledComponents';
import { FoundationsProps } from '../Foundations';

const FoundationsForms = ({ selected }: FoundationsProps): JSX.Element => {
  return (
    <TabPanel id="foundations-forms" selected={selected}>
      <StyledTabPanelIntro>
        <BodyText size="medium" style={{ marginBottom: 'var(--spacing-6)' }}>
          In this section, we define patterns, props, attributes, and
          sub-components common to various form fields. This will help designers
          and developers to create forms quickly and efficiently.
        </BodyText>
      </StyledTabPanelIntro>

      <StyledTabPanelText>
        <Heading size="xx-small">Form fields</Heading>
        <BodyText size="small" style={{ marginBottom: 'var(--spacing-5)' }}>
          A &quot;form field&quot; is any of our form components (eg: Input,
          Textarea, Select, Choicelist) that control their own labels, assistive
          text, and error messaging.
        </BodyText>
      </StyledTabPanelText>

      <StyledCardGrid
        desktopColumns={3}
        style={{ paddingBottom: 'var(--spacing-5)' }}
      >
        <Card size="small">
          <Heading as="h3" size="x-small">
            Input
          </Heading>
          <BodyText size="small">Basic, single-line text input field.</BodyText>
          <Input
            id="form-documentation-input"
            label="An Input field"
            placeholder="Enter a mythical beast"
          />
        </Card>
        <Card size="small">
          <Heading as="h3" size="x-small">
            Textarea
          </Heading>
          <BodyText size="small">
            Multi-line, plain text editing control.
          </BodyText>
          <Textarea
            id="form-documentation-textarea"
            label="A Textarea field"
            placeholder="Describe your Bigfoot encounter experience"
          />
        </Card>
        <Card size="small">
          <Heading as="h3" size="x-small">
            Select
          </Heading>
          <BodyText size="small">
            Presents a floating menu of multiple options.
          </BodyText>
          <Select
            id="form-documentation-select"
            label="Choose a mythical beast"
          >
            <option>Bigfoot</option>
            <option>Baconface</option>
            <option>Superchicken</option>
            <option>Megachicken</option>
            <option>The Loch Ness souvenir shop ghost</option>
            <option>Ickis from AAAHH!!! Real Monsters</option>
          </Select>
        </Card>
        <Card size="small">
          <Heading as="h3" size="x-small">
            ChoiceList
          </Heading>
          <BodyText size="small">
            Organizes and groups a set of Radio or Checkbox fields within a
            form.
          </BodyText>
          <ChoiceList
            fieldSize="medium"
            id="favorite-ghostbuster"
            legend="Favorite Ghostbuster"
          >
            <Radio
              checked
              fieldSize="medium"
              id="favorite-ghostbuster--ray-stanz"
              label="Ray Stanz"
              name="favorite-ghostbuster"
              value="ray-stanz"
            />
            <Radio
              fieldSize="medium"
              id="favorite-ghostbuster--egon-spengler"
              label="Egon Spengler"
              name="favorite-ghostbuster"
              value="egon-spengler"
            />
            <Radio
              fieldSize="medium"
              id="favorite-ghostbuster--peter-venkman"
              label="Peter Venkman"
              name="favorite-ghostbuster"
              value="peter-venkman"
            />
            <Radio
              fieldSize="medium"
              id="favorite-ghostbuster--winston-zeddemore"
              label="Winston Zeddemore"
              name="favorite-ghostbuster"
              value="winston-zeddemore"
            />
          </ChoiceList>
        </Card>
        <Card size="small">
          <Heading as="h3" size="x-small">
            Radio
          </Heading>
          <BodyText size="small">
            Usually placed inside a Choicelist with other Radio buttons, allows
            a single selection from a list.
          </BodyText>
          <Radio
            checked
            id="form-documentation-radio"
            label="Five golden rings"
            value="form-documentation-radio"
          />
        </Card>
        <Card size="small">
          <Heading as="h3" size="x-small">
            Checkbox
          </Heading>
          <BodyText size="small">
            Used alone or inside a Choicelist with other Checkboxes, allows
            single, multiple, or no selections.
          </BodyText>
          <Checkbox
            checked
            id="form-documentation-checkbox"
            label="Opt-in to helicopter"
            value="form-documentation-checkbox"
          />
        </Card>
      </StyledCardGrid>

      <StyledTabPanelText>
        <Heading size="xx-small">Field support components</Heading>
        <BodyText size="small">
          These are any of our form components that are utilized and controlled
          by a form field, but are also available to use by themselves in rare,
          one-off cases.
        </BodyText>
      </StyledTabPanelText>

      <StyledCardGrid
        desktopColumns={3}
        style={{ paddingBottom: 'var(--spacing-5)' }}
      >
        <Card size="small">
          <Heading as="h3" size="x-small">
            FieldLabel
          </Heading>
          <BodyText size="small">
            Label appearing above a form field, utilizing the{' '}
            <Code>&lt;label&gt;</Code> property.
          </BodyText>
          <Input
            id="form-documentation-field-label"
            label="This is a field label"
            placeholder="Labelled and ready to rock"
          />
        </Card>
        <Card size="small">
          <Heading as="h3" size="x-small">
            FieldMessage
          </Heading>
          <BodyText size="small">
            Descriptive/assistive text appearing below a field.
          </BodyText>
          <Input
            id="form-documentation-field-message"
            message="This is help text about a field"
            placeholder="I have a message for you"
          />
        </Card>
        <Card size="small">
          <Heading as="h3" size="x-small">
            FieldError
          </Heading>
          <BodyText size="small">
            Error message and icon appearing below a field.
          </BodyText>
          <Input
            error
            errorMessage="This is a scary field error"
            id="form-documentation-field-error"
            placeholder="There's an error below me"
          />
        </Card>
      </StyledCardGrid>

      <StyledTabPanelText>
        <Heading size="xx-small">Common form field properties</Heading>
        <BodyText size="small">
          Below are props, attributes, and sub-components common to various form
          fields.
        </BodyText>

        <Heading as="h3" size="xx-small">
          Placeholder
        </Heading>

        <ul
          style={{
            padding: 'var(--spacing-2) 0 var(--spacing-5) var(--spacing-4)',
          }}
        >
          <li>
            A placeholder is text that appears in the field when it has no value
            set. It&apos;s controlled by the <Code>placeholder</Code> attribute.
          </li>
          <li>
            Don&apos;t depend on placeholder alone to describe a field. Be sure
            to specify a <Code>label</Code> or another element the field is{' '}
            <Code>labelledBy</Code>.{' '}
            <a
              href="https://www.nngroup.com/articles/form-design-placeholders"
              target="_blank"
              rel="noreferrer"
            >
              (NNG)
            </a>
          </li>
          <li>
            Placeholder text should be something non-critical to properly using
            the field. Think &quot;Enter a password&quot; vs &quot;Your password
            should be 600 characters long&quot;. Utilize <Code>message</Code>{' '}
            for assistive text.
          </li>
        </ul>

        <Heading as="h3" size="xx-small">
          FieldSize
        </Heading>

        <ul
          style={{
            padding: 'var(--spacing-2) 0 var(--spacing-5) var(--spacing-4)',
          }}
        >
          <li>
            Fields can be <Code>small</Code>, <Code>medium</Code>, or{' '}
            <Code>large</Code>.
          </li>
          <li>
            <Code>small</Code> fields are useful for putting forms in tight
            spaces, like small Cards, sidebars, or other places where they need
            a slight visual deemphasis.
          </li>
          <li>
            <Code>medium</Code> fields (the default field size) are standard.
            Use them when creating a form that&apos;s &quot;in the open&quot; or
            is the main focus of a page.
          </li>
          <li>
            <Code>large</Code> fields are big kids that eat{' '}
            <a
              href="https://youtu.be/yDZK6H3d5bk"
              target="_blank"
              rel="noreferrer"
            >
              big cereal
            </a>
            . They should be used sparingly. They&apos;ll feel too large if used
            in a typical form, but are useful for placing high-emphasis on
            minimal forms such as a login page or a full-page flow.
          </li>
        </ul>

        <Heading as="h3" size="xx-small">
          Required fields
        </Heading>

        <ul
          style={{
            padding: 'var(--spacing-2) 0 var(--spacing-5) var(--spacing-4)',
          }}
        >
          <li>
            Set the <Code>required</Code> boolean to true on a form field to
            display a &quot;required&quot; asterisk (*) to indicate to users
            (and screen readers) that a field is required. * Required fields
            must be filled out (or selected/checked) to continue.
          </li>
          <li>
            If a required field is left blank on submission, utilize the{' '}
            <Code>errorMessage</Code> to communicate its status to the user.
          </li>
          <li>
            Include a required asterisk on all required fields. If your form has
            many required fields, reconsider the blocker you&apos;re potentially
            placing in front of a user.
          </li>
          <li>
            In a short form where most or all fields are required (such as a
            page of a wizard flow or a new campaign creation screen), the
            required asterisk may not be necessary. However, be sure to indicate
            to the user in another way that they must fill out each field to
            continue.
          </li>
        </ul>

        <Heading as="h3" size="xx-small">
          Disabled fields
        </Heading>

        <ul
          style={{
            padding: 'var(--spacing-2) 0 var(--spacing-5) var(--spacing-4)',
          }}
        >
          <li>
            Set the <Code>disabled</Code> boolean to true on a form field to
            disable its functionality and visually indicate its state.
          </li>
          <li>
            If a required field is left blank on submission, utilize the{' '}
            <Code>errorMessage</Code> to communicate its status to the user.
          </li>
          <li>
            <strong>Note:</strong> The term &quot;disabled&quot; should only be
            used in reference to the pre-existing field attribute. Be inclusive,
            friendly humans!
          </li>
        </ul>

        <Heading as="h3" size="xx-small">
          Readonly fields
        </Heading>

        <ul
          style={{
            padding: 'var(--spacing-2) 0 var(--spacing-5) var(--spacing-4)',
          }}
        >
          <li>
            Set the <Code>readOnly</Code> boolean to true on a form field to
            make it read only (no movies or tv). These fields have values but
            are not editable.
          </li>
        </ul>

        <Heading size="xx-small">Placement and spacing</Heading>

        <ul
          style={{
            padding: 'var(--spacing-2) 0 var(--spacing-5) var(--spacing-4)',
          }}
        >
          <li>
            Form fields should almost always be stacked and grouped
            appropriately with related fields.
          </li>
          <li>
            Utilize appropriate margins between field elements.{' '}
            <a
              href="https://www.nngroup.com/articles/form-design-white-space"
              target="_blank"
              rel="noreferrer"
            >
              (NNG)
            </a>
          </li>
          <li>Space out form fields using our 6px baseline.</li>
          <li>
            In CSS, use spacing variables (eg: <Code>var(--spacing-3)</Code>) to
            control spacing.
          </li>
          <li>
            Typically use bottom margin to add space between consecutive fields.
          </li>
          <li>
            Expect users to read vertically. Stack fields whenever possible to
            ensure optimum scannability.{' '}
            <a
              href="https://www.nngroup.com/articles/form-design-white-space"
              target="_blank"
              rel="noreferrer"
            >
              (NNG)
            </a>
          </li>
          <li>
            As a (very) general rule, add 18px of space (
            <Code>var(--spacing-3)</Code>) between form fields.
          </li>
        </ul>

        <Heading size="xx-small">Handling selection and choices</Heading>

        <BodyText size="small">
          <Code>Select</Code>, <Code>DropdownButton</Code> menus, and{' '}
          <Code>Choicelist</Code> are all list of choices
          (&quot;droppy-listy-choosies&quot;). Below are some general guidelines
          for usage.
        </BodyText>

        <Heading as="h3" size="xx-small">
          Select vs DropdownButton
        </Heading>

        <ul
          style={{
            padding: 'var(--spacing-2) 0 var(--spacing-5) var(--spacing-4)',
          }}
        >
          <li>Use Select in all forms.</li>
          <li>Select stores a value in its field.</li>
          <li>DropdownButton actions can change the page or do a thing.</li>
          <li>
            DropdownButton does not change its Button text value when a list
            item is selected. List items will do a thing.
          </li>
        </ul>

        <Heading as="h3" size="xx-small">
          Select vs Choicelist
        </Heading>

        <ul
          style={{
            padding: 'var(--spacing-2) 0 var(--spacing-5) var(--spacing-4)',
          }}
        >
          <li>
            Both a Select menu and a Choicelist with Checkboxes or Radios are
            useful for choosing from number of options. Below are some general
            guidelines for usage.
          </li>
          <li>Use a single Select when choosing one of 5-7+ options.</li>
          <li>
            Use a Choicelist with Radios when choosing from one of &lt; 5
            options, keeping all options visible on-screen. Replace with a
            Select menu if the persistent visibility of the options becomes
            cluttering or overbearing.
          </li>
          <li>Use a multi Select when choosing one or more of 5-7+ options.</li>
          <li>
            Use a Choicelist with Checkboxes when choosing one or more of &lt; 5
            options, keeping all options visible on-screen. Replace with a
            Select menu if the persistent visibility of the options becomes
            cluttering or overbearing.
          </li>
        </ul>
      </StyledTabPanelText>
    </TabPanel>
  );
};

export default FoundationsForms;
